import { Box, Column } from "@stenajs-webui/core";
import { Banner, PrimaryButton } from "@stenajs-webui/elements";
import { ModalFooter } from "@stenajs-webui/modal";
import { useCallback } from "react";
import { useSelector } from "react-redux";

import {
  type InformationMessage,
  resolveInformationMessageBannerVariant,
} from "../../../../../common-ui/information-message/informationMessageUtils";
import {
  PayButton,
  SeePaymentLink,
} from "../../../../../common-ui/payment/PayButton";
import type { BasicBooking } from "../../../../../common/graphql/fragments/gql/BasicBooking";
import { useShowCalculatePrice } from "../../../../../common/pricing/useShowCalculatePrice";
import {
  type TrackerCategory,
  trackEvent,
} from "../../../../../common/tracking/trackerService";
import { useUpdateFormRevertChanges } from "../../../../../common/update-information/hooks/useUpdateFormRevertChanges";
import { useUpdateFormState } from "../../../../../common/update-information/hooks/useUpdateFormState";
import { PRINT_HIDE_CLASSNAME } from "../../../../../globalStyles";
import { setSaveAsWaitList } from "../../../../../services/update-form/updateFormActions";
import {
  getEditedFormState,
  getFormIsEdited,
  type UpdateFormIdVariant,
} from "../../../../../services/update-form/updateFormReducer";
import { isBookingEditable } from "../../../../../services/update-form/utils/bookingUtils";
import { useAppDispatch } from "../../../../../store/appDispatch";
import type { StoreState } from "../../../../../store/storeState";
import { CalculatePriceForDetails } from "../../pricing/CalculatePriceForDetails";
import {
  bookingHasPayments,
  bookingIsPayable,
} from "../../utils/showPaymentUtil";
import { BookingCancelButton } from "../BookingCancelButton";
import { BookingUpdateStatusMessage } from "../BookingUpdateStatusMessage";

const trackerCategory: TrackerCategory = "Details";
const formId: UpdateFormIdVariant = "Details";

export interface BookingDetailsEditFooterProps {
  booking: BasicBooking;
  onCancelFail: () => void;
  statusInformationList: InformationMessage[];
}

export function BookingDetailsEditFooter({
  booking,
  onCancelFail,
  statusInformationList,
}: BookingDetailsEditFooterProps) {
  const dispatch = useAppDispatch();
  const mapState = useCallback(
    (state: StoreState) => {
      return getFormIsEdited(state, {
        bookingNo: booking.bookingNo,
        formId: "Details",
      });
    },
    [booking.bookingNo],
  );

  const { submitting, submit, updateSubmitInformation } = useUpdateFormState({
    bookingNo: booking.bookingNo,
    formId,
  });

  const revertChanges = useUpdateFormRevertChanges({
    bookingNo: booking.bookingNo,
    formId,
  });

  const edited = useSelector(mapState);

  const editedFormState = useSelector(
    useCallback(
      (state: StoreState) => {
        return getEditedFormState(state, {
          bookingNo: booking.bookingNo,
          formId,
        });
      },
      [booking.bookingNo],
    ),
  );

  const showCalculatePrice = useShowCalculatePrice(
    editedFormState?.customer?.isCashCustomer,
    booking.bookingStatus.status,
  );

  const updateBookingToWaitlist = () => {
    dispatch(setSaveAsWaitList({ bookingNo: booking.bookingNo, formId }));
    submit();
    trackEvent({ category: formId, action: "SaveToWaitList" });
  };

  const isEditable = isBookingEditable(booking);
  const showSeePaymentLink = bookingHasPayments(booking);
  const showPayButton = bookingIsPayable(booking);

  return (
    <ModalFooter sticky className={PRINT_HIDE_CLASSNAME}>
      <Column flex={1} gap={2}>
        <BookingUpdateStatusMessage
          onIgnoreChanges={revertChanges}
          onSetBookingStatusToWaitlist={updateBookingToWaitlist}
          submitting={submitting}
          updateSubmitInformation={updateSubmitInformation}
        />
        {statusInformationList.map((statusInformation, index) => (
          <Banner
            key={index}
            variant={resolveInformationMessageBannerVariant(
              statusInformation.type,
            )}
            text={statusInformation.message}
          />
        ))}
        <Box
          justifyContent={"space-between"}
          flexDirection={["column", null, "row"]}
          flex={1}
          gap
        >
          {booking.cancelable && (
            <Box width={["100%", null, "auto"]}>
              <BookingCancelButton
                booking={booking}
                onFail={onCancelFail}
                disabled={submitting}
              />
            </Box>
          )}
          <Box gap={1} flexDirection={["column", null, "row"]}>
            {showSeePaymentLink && (
              <SeePaymentLink
                bookingNo={booking.bookingNo}
                trackerCategory={trackerCategory}
              />
            )}
            {isEditable && (
              <>
                {showCalculatePrice && (
                  <CalculatePriceForDetails
                    formState={editedFormState}
                    placement={"top"}
                  />
                )}
                {showPayButton && (
                  <PayButton
                    bookingNo={booking.bookingNo}
                    trackerCategory={trackerCategory}
                    disabled={edited}
                  />
                )}
                <PrimaryButton
                  disabled={!edited}
                  loading={submitting}
                  className={"t_save_button"}
                  label={"Save"}
                  loadingLabel={"Saving..."}
                  onClick={submit}
                />
              </>
            )}
          </Box>
        </Box>
      </Column>
    </ModalFooter>
  );
}
